import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Textarea } from "./ui/textarea";
import { Mail, Phone, MapPin, Loader2, CheckCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContactSection = () => {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [honeypot, setHoneypot] = useState(""); // Anti-spam honeypot
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    interest: "",
    message: ""
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Anti-spam: If honeypot is filled, it's a bot
    if (honeypot) {
      toast.success("Message sent successfully!"); // Fake success for bots
      return;
    }
    
    setLoading(true);

    try {
      await axios.post(`${API}/contacts`, formData);
      setSubmitted(true);
      toast.success("Message sent successfully!");
    } catch (error) {
      toast.error("Failed to send message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const contactInfo = [
    {
      icon: Mail,
      label: "Email",
      value: "info@thefinrun.com",
      href: "mailto:info@thefinrun.com"
    },
    {
      icon: Phone,
      label: "Phone",
      value: "+91 9618474761",
      href: "tel:+919618474761"
    },
    {
      icon: MapPin,
      label: "Office",
      value: "Hyderabad, Telangana, India",
      href: null
    }
  ];

  return (
    <section id="contact" className="section-spacing bg-[#111827]" data-testid="contact-section">
      <div className="container-custom">
        <div className="grid lg:grid-cols-2 gap-16">
          {/* Left Side - Info */}
          <div>
            <p className="text-[#FFD700] text-sm uppercase tracking-[0.2em] font-bold mb-4">
              Get In Touch
            </p>
            <h2 className="text-4xl sm:text-5xl font-bold text-white font-['Outfit'] mb-6">
              Run With Us
            </h2>
            <p className="text-gray-400 text-lg mb-12">
              Have questions about registration, sponsorship, or volunteering? 
              We'd love to hear from you. Reach out and join the movement!
            </p>

            {/* Contact Info Cards */}
            <div className="space-y-6 mb-12">
              {contactInfo.map((item, i) => {
                const Icon = item.icon;
                return (
                  <div key={i} className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-[#FF3B30]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Icon className="w-6 h-6 text-[#FF3B30]" />
                    </div>
                    <div>
                      <p className="text-gray-500 text-sm">{item.label}</p>
                      {item.href ? (
                        <a 
                          href={item.href}
                          className="text-white font-semibold hover:text-[#FFD700] transition-colors"
                        >
                          {item.value}
                        </a>
                      ) : (
                        <p className="text-white font-semibold">{item.value}</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Social Links */}
            <div>
              <p className="text-gray-500 text-sm mb-4">Follow us on social media</p>
              <div className="flex gap-4">
                {["Instagram", "Twitter", "Facebook", "LinkedIn"].map((social, i) => (
                  <a
                    key={i}
                    href="#"
                    className="w-12 h-12 bg-white/5 rounded-xl flex items-center justify-center text-white hover:bg-[#FF3B30] transition-colors"
                    data-testid={`social-${social.toLowerCase()}`}
                  >
                    <span className="text-sm font-bold">{social.charAt(0)}</span>
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Right Side - Form */}
          <div className="bg-white rounded-3xl p-8">
            {submitted ? (
              <div className="text-center py-12" data-testid="contact-success">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-10 h-10 text-green-600" />
                </div>
                <h3 className="text-2xl font-bold text-[#0A0A0A] font-['Outfit'] mb-4">
                  Message Sent!
                </h3>
                <p className="text-[#4B5563] mb-6">
                  Thank you for reaching out. Our team will get back to you within 24-48 hours.
                </p>
                <Button 
                  onClick={() => {
                    setSubmitted(false);
                    setFormData({
                      first_name: "",
                      last_name: "",
                      email: "",
                      phone: "",
                      interest: "",
                      message: ""
                    });
                  }}
                  variant="outline"
                  className="rounded-full"
                >
                  Send Another Message
                </Button>
              </div>
            ) : (
              <>
                <h3 className="text-2xl font-bold text-[#0A0A0A] font-['Outfit'] mb-6">
                  Send us a message
                </h3>
                <form onSubmit={handleSubmit} className="space-y-5">
                  {/* Honeypot field - hidden from humans, catches bots */}
                  <input
                    type="text"
                    name="website"
                    value={honeypot}
                    onChange={(e) => setHoneypot(e.target.value)}
                    style={{ position: 'absolute', left: '-9999px' }}
                    tabIndex="-1"
                    autoComplete="off"
                    aria-hidden="true"
                  />
                  
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="first_name">First Name *</Label>
                      <Input 
                        id="first_name"
                        value={formData.first_name}
                        onChange={(e) => handleChange("first_name", e.target.value)}
                        required
                        className="rounded-xl"
                        data-testid="contact-first-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="last_name">Last Name *</Label>
                      <Input 
                        id="last_name"
                        value={formData.last_name}
                        onChange={(e) => handleChange("last_name", e.target.value)}
                        required
                        className="rounded-xl"
                        data-testid="contact-last-name"
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="email">Email *</Label>
                      <Input 
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleChange("email", e.target.value)}
                        required
                        className="rounded-xl"
                        data-testid="contact-email"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone *</Label>
                      <Input 
                        id="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => handleChange("phone", e.target.value)}
                        required
                        className="rounded-xl"
                        data-testid="contact-phone"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="interest">I'm interested in *</Label>
                    <Select 
                      value={formData.interest} 
                      onValueChange={(v) => handleChange("interest", v)}
                    >
                      <SelectTrigger className="rounded-xl" data-testid="contact-interest">
                        <SelectValue placeholder="Select an option" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="participation">Participating in the run</SelectItem>
                        <SelectItem value="volunteering">Volunteering</SelectItem>
                        <SelectItem value="sponsorship">Sponsorship opportunities</SelectItem>
                        <SelectItem value="partnership">Partnership/Collaboration</SelectItem>
                        <SelectItem value="media">Media/Press inquiry</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message">Message *</Label>
                    <Textarea 
                      id="message"
                      placeholder="Tell us how we can help..."
                      value={formData.message}
                      onChange={(e) => handleChange("message", e.target.value)}
                      required
                      rows={4}
                      className="rounded-xl"
                      data-testid="contact-message"
                    />
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full btn-primary py-6 text-lg"
                    disabled={loading}
                    data-testid="contact-submit-btn"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      "Send Message"
                    )}
                  </Button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
